import React, { Component, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LogIn, RefreshCw, TriangleAlert, Wifi, WifiOff } from 'lucide-react';
import { FournisseurRetours, annoncer } from './Retours';
import { BarreNavigation } from './Chargement';

/* ═══════════════════════════════════════════════════════════════════════
   6. ÉTATS DU SYSTÈME — réseau, session, erreur fatale

   Montés une fois, à la racine, par `SystemeEtats`. Chacun écoute un
   signal précis et ne s'affiche que lorsqu'il a quelque chose à dire.

     assopro:session-expiree  la couche API n'a pas pu renouveler le jeton
     assopro:incident         le serveur ne répond pas / répond 5xx
     online / offline         le navigateur perd ou retrouve le réseau
   ═══════════════════════════════════════════════════════════════════════ */

export const EVT_SESSION_EXPIREE = 'assopro:session-expiree';
export const EVT_INCIDENT = 'assopro:incident';
export const signalerSessionExpiree = () => window.dispatchEvent(new CustomEvent(EVT_SESSION_EXPIREE));

/** Hors ligne → reconnexion → rétabli. */
function BandeauReseau() {
  const [enLigne, setEnLigne] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [retabli, setRetabli] = useState(false);
  const minuterie = useRef(null);

  useEffect(() => {
    const perdu = () => { setEnLigne(false); setRetabli(false); };
    const retrouve = () => {
      setEnLigne(true);
      setRetabli(true);
      clearTimeout(minuterie.current);
      minuterie.current = setTimeout(() => setRetabli(false), 3500);
    };
    window.addEventListener('offline', perdu);
    window.addEventListener('online', retrouve);
    return () => {
      window.removeEventListener('offline', perdu);
      window.removeEventListener('online', retrouve);
      clearTimeout(minuterie.current);
    };
  }, []);

  if (enLigne && !retabli) return null;
  return (
    <div role={enLigne ? 'status' : 'alert'}
         className={`fixed top-3 left-1/2 -translate-x-1/2 z-[260] flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold shadow-lg ${
           enLigne ? 'bg-[var(--vert-pale,#EAF6E7)] text-[var(--vert-fonce,#2F7A2A)]' : 'bg-[#0A1128] text-white'}`}>
      {enLigne
        ? <><Wifi className="w-4 h-4" aria-hidden="true" />Connexion rétablie</>
        : <><WifiOff className="w-4 h-4" aria-hidden="true" />Hors ligne — vos actions reprendront au retour du réseau</>}
    </div>
  );
}

/** La session a expiré : on le dit, et on propose de se reconnecter
 *  en revenant ensuite exactement ici. */
function FenetreSessionExpiree({ cheminConnexion, clesSession }) {
  const [ouverte, setOuverte] = useState(false);
  useEffect(() => {
    const ouvrir = () => setOuverte(true);
    window.addEventListener(EVT_SESSION_EXPIREE, ouvrir);
    return () => window.removeEventListener(EVT_SESSION_EXPIREE, ouvrir);
  }, []);
  if (!ouverte) return null;

  const reconnecter = () => {
    clesSession.forEach((c) => { try { localStorage.removeItem(c); } catch { /* stockage indisponible */ } });
    const retour = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`${cheminConnexion}?retour=${retour}`);
  };

  return (
    <div className="fixed inset-0 z-[270] grid place-items-center p-4 bg-black/55 backdrop-blur-[2px]">
      <div role="alertdialog" aria-modal="true" aria-labelledby="session-titre"
           className="w-full max-w-sm rounded-[var(--r-carte,22px)] bg-[var(--bg-card,#fff)] p-6 text-center shadow-2xl">
        <span className="w-12 h-12 mx-auto rounded-full grid place-items-center bg-[var(--attention-fond)] text-[var(--attention)]">
          <LogIn className="w-6 h-6" aria-hidden="true" />
        </span>
        <h2 id="session-titre" className="text-[17px] font-semibold text-[var(--text-bold)] mt-3 mb-1">Votre session a expiré</h2>
        <p className="text-[13.5px] text-[var(--text-muted)] m-0">
          Par sécurité, vous avez été déconnecté après une période d’inactivité. Reconnectez-vous : vous reviendrez sur cette page.
        </p>
        <button type="button" onClick={reconnecter} className="act w-full mt-5" autoFocus>
          <LogIn className="w-4 h-4" /> Se reconnecter
        </button>
      </div>
    </div>
  );
}

/** Le serveur ne répond pas : un bandeau en haut, retiré dès que ça remarche.
 *  (Le back-office a déjà le sien dans `AdminLayout` : `avecIncident={false}`.) */
function BandeauIncident() {
  const [message, setMessage] = useState(null);
  useEffect(() => {
    const ecouter = (e) => setMessage(e.detail?.message || null);
    window.addEventListener(EVT_INCIDENT, ecouter);
    return () => window.removeEventListener(EVT_INCIDENT, ecouter);
  }, []);
  if (!message) return null;
  return (
    <div role="alert" className="fixed top-0 inset-x-0 z-[255] flex items-center justify-center gap-2 bg-[var(--alerte-fond)] text-[var(--alerte)] px-4 py-2 text-[13px] font-semibold">
      <TriangleAlert className="w-4 h-4 shrink-0" aria-hidden="true" />{message}
    </div>
  );
}

/** Une erreur de rendu ne doit pas laisser une page blanche.
 *  `cle` (ex. le chemin) remet le filet à zéro à la navigation suivante. */
export class FiltreErreurs extends Component {
  constructor(props) { super(props); this.state = { erreur: null }; }

  static getDerivedStateFromError(erreur) { return { erreur }; }

  componentDidCatch(erreur, info) {
    // eslint-disable-next-line no-console
    console.error('Erreur d’affichage :', erreur, info?.componentStack);
  }

  componentDidUpdate(prec) {
    if (this.state.erreur && prec.cle !== this.props.cle) this.setState({ erreur: null });
  }

  render() {
    if (!this.state.erreur) return this.props.children;
    return (
      <div role="alert" className="min-h-[50vh] grid place-items-center p-6">
        <div className="max-w-md text-center">
          <span className="w-14 h-14 mx-auto rounded-full grid place-items-center bg-[var(--alerte-fond)] text-[var(--alerte)]">
            <TriangleAlert className="w-7 h-7" aria-hidden="true" />
          </span>
          <h2 className="text-[18px] font-semibold text-[var(--text-bold)] mt-4 mb-1">Cette page n’a pas pu s’afficher</h2>
          <p className="text-[13.5px] text-[var(--text-muted)] m-0">
            Une erreur inattendue s’est produite. Vos données ne sont pas perdues : réessayez, ou rechargez la page.
          </p>
          <div className="flex justify-center gap-2 mt-5 flex-wrap">
            <button type="button" className="act act-contour" onClick={() => this.setState({ erreur: null })}>Réessayer</button>
            <button type="button" className="act" onClick={() => window.location.reload()}><RefreshCw className="w-4 h-4" />Recharger la page</button>
          </div>
        </div>
      </div>
    );
  }
}

/** Le filet par page : remis à zéro à chaque changement d'adresse. */
export function FiltreErreursDePage({ children }) {
  const { pathname } = useLocation();
  return <FiltreErreurs cle={pathname}>{children}</FiltreErreurs>;
}

/** Tout le système, à monter une fois sous le routeur. */
export function SystemeEtats({ children, cheminConnexion, clesSession = [], avecIncident = true }) {
  useEffect(() => {
    // Trop de requêtes : un toast suffit, l'écran garde ce qu'il montre.
    const limite = () => annoncer('Trop de demandes en peu de temps. Patientez une minute, puis réessayez.', { ton: 'attention' });
    window.addEventListener('assopro:limite', limite);
    return () => window.removeEventListener('assopro:limite', limite);
  }, []);
  return (
    <FournisseurRetours>
      <BarreNavigation />
      <BandeauReseau />
      {avecIncident && <BandeauIncident />}
      <FenetreSessionExpiree cheminConnexion={cheminConnexion} clesSession={clesSession} />
      <FiltreErreursDePage>{children}</FiltreErreursDePage>
    </FournisseurRetours>
  );
}
